import { ChevronDown, ChevronRight, PhoneIncoming02, PhoneOutgoing02 } from '@untitled-ui/icons-react/build/cjs';
import React, { useState } from 'react';
import CheckCircleDark from 'src/assets/img/icons/filled-icons/check-circle-dark.png';
import inboundEmailIcon from 'src/assets/img/icons/mail-arrow-down-dark-gray.svg';
import outboundEmailIcon from 'src/assets/img/icons/mail-arrow-up-dark-gray.svg';
import verifiedIcon from 'src/assets/img/verified-tick.svg';

interface StepItem {
    // Optional: loadboard-icon items render only a value (no label), so the component guards every label usage with `item.label && …`
    label?: string;
    icon?: 'verified' | React.ReactNode;
    actionType?: 'inboundCall' | 'outboundCall' | 'outboundEmail' | 'inboundEmail';
    rightSideValue?: React.ReactNode;
    value?: string;
    status?: 'completed' | 'loading';
    onClick?: () => void;
    /** When true, renders a green check circle on the right instead of CheckCircleDark */
    isPosted?: boolean;
}

export interface Step {
    title: string;
    /** Right-aligned action rendered on the title row (e.g. a Stop button) */
    titleAction?: React.ReactNode;
    items?: StepItem[];
    isValuePrice?: boolean;
    isList?: boolean;
    fixWidth?: boolean;
}

interface IProps {
    isList?: boolean;
    steps: Step[];
    defaultExpanded?: boolean;
}

const AiWorkflow = ({ steps, defaultExpanded }: IProps) => {
    const [openSteps, setOpenSteps] = useState<Record<number, boolean>>(defaultExpanded ? Object.fromEntries(steps.map((_, i) => [i, true])) : {});

    const toggleStep = (index: number) => {
        setOpenSteps((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    return (
        <div className="relative ai-sender">
            <div
                style={{
                    display: 'grid',
                    gridTemplateRows: '1fr',
                    transition: 'grid-template-rows 0.25s ease',
                }}
            >
                <div className="overflow-hidden">
                    <ul>
                        {steps.map((step, index) => (
                            <li
                                key={index}
                                className={`flex gap-x-2 [&:last-child:not(:only-child)]:border-b-0  pb-1.5 relative ${
                                    openSteps[index] === true ? '' : 'border-b border-utilityGray200 pb-1.5 mb-1.5 [&:last-child:not(:only-child)]:border-b-0'
                                }`}
                            >
                                <div>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                        <path
                                            d="M0.649994 0.649902V1.58324C0.649994 3.82345 0.649994 4.94355 1.08597 5.7992C1.46946 6.55185 2.08138 7.16377 2.83403 7.54726C3.68968 7.98324 4.80978 7.98324 7.04999 7.98324H11.3167M7.98333 11.3166L11.3167 7.98324L7.98333 4.6499"
                                            stroke="#D0D5DD"
                                            strokeWidth="1.3"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>

                                <div className="flex-1 min-w-0 max-w-[400px]">
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleStep(index);
                                        }}
                                        className="flex items-center gap-x-1 cursor-pointer"
                                    >
                                        <p className="truncate text-textSecondary text-xs font-normal flex-1">{step.title}</p>
                                        {/* stopPropagation so the action doesn't toggle the step open/closed */}
                                        {step.titleAction && (
                                            <div className="shrink-0 flex flex-col" onClick={(e) => e.stopPropagation()}>
                                                {step.titleAction}
                                            </div>
                                        )}

                                        {step.items && step.items.length > 0 && (
                                            <ChevronDown className={`w-4 h-4 transition-transform duration-200 text-grayLight600 flex-none ${openSteps[index] === true ? 'rotate-180' : ''}`} />
                                        )}
                                    </div>

                                    {/* STEP CONTENT */}
                                    {step.items && step.items.length > 0 && (
                                        <div
                                            style={{
                                                display: 'grid',
                                                gridTemplateRows: openSteps[index] === true ? '1fr' : '0fr',
                                                transition: 'grid-template-rows 0.25s ease',
                                            }}
                                        >
                                            <div className={`overflow-hidden`}>
                                                <div className="pb-1 pt-1.5">
                                                    {/* ITEMS */}
                                                    {step.items && (
                                                        <ul className={step.isList ? 'agent-message-stepper-lists' : 'agent-message-stepper-row-lists'}>
                                                            {step.items.map((item, i) => (
                                                                <li
                                                                    key={i}
                                                                    onClick={item.onClick}
                                                                    className={`${
                                                                        step.isList
                                                                            ? 'agent-message-stepper-list-text'
                                                                            : 'agent-message-stepper-row-list agent-message-stepper-row-text overflow-hidden'
                                                                    } ${item.onClick ? 'cursor-pointer transition-all duration-300 hover:bg-gray200' : ''} `}
                                                                >
                                                                    {!step.isList && (
                                                                        <>
                                                                            {item.icon && item.icon !== 'verified' && (
                                                                                <div className="w-4 h-4 rounded-full border border-utilityBlack border-opacity-[0.08] bg-white flex items-center justify-center flex-none overflow-hidden">
                                                                                    <img src={item.icon as string} className="w-full h-full object-contain" />
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    )}

                                                                    <div className={step.isList ? '' : 'agent-message-stepper-row-list-content overflow-hidden'}>
                                                                        {item.label && (
                                                                            <span
                                                                                className={`${step.isValuePrice ? 'truncate' : ''} ${
                                                                                    step.isList
                                                                                        ? ''
                                                                                        : `agent-message-stepper-row-list-label overflow-hidden ${step.fixWidth ?? true ? 'w-[80px] flex-none' : ''}`
                                                                                }`}
                                                                            >
                                                                                {item.label}

                                                                                {item.label && step.isList && <>&nbsp;-&nbsp;</>}
                                                                            </span>
                                                                        )}

                                                                        {item.value !== undefined && item.value !== null && (
                                                                            <span
                                                                                className={
                                                                                    step.isList
                                                                                        ? 'agent-message-stepper-list-value'
                                                                                        : `agent-message-stepper-row-list-value ${step.isValuePrice ? '' : 'truncate'}`
                                                                                }
                                                                            >
                                                                                {item.value}
                                                                            </span>
                                                                        )}
                                                                        {item.icon === 'verified' && (
                                                                            <figure>
                                                                                <img src={verifiedIcon} className="w-3 h-3" />
                                                                            </figure>
                                                                        )}
                                                                        {!step.isList &&
                                                                            (item.isPosted || item.status === 'completed' ? (
                                                                                <img src={CheckCircleDark} alt="check" className="w-3.5 h-3.5" />
                                                                            ) : item.status === 'loading' ? (
                                                                                <span className="loading loading-spinner w-4 h-4 text-primary"></span>
                                                                            ) : null)}
                                                                    </div>
                                                                    {!step.isList && (item.actionType || item.onClick) && (
                                                                        <div className="flex items-center gap-x-1">
                                                                            {item.actionType === 'inboundCall' ? (
                                                                                <PhoneIncoming02 color="#475467" className="w-3.5 h-3.5 text-gray500" />
                                                                            ) : item.actionType === 'outboundCall' ? (
                                                                                <PhoneOutgoing02 color="#475467" className="w-3.5 h-3.5 text-gray500" />
                                                                            ) : item.actionType === 'outboundEmail' ? (
                                                                                <img src={outboundEmailIcon} alt="Outbound Email" className="w-3.5 h-3.5 text-gray500" />
                                                                            ) : item.actionType === 'inboundEmail' ? (
                                                                                <img src={inboundEmailIcon} alt="Inbound Email" className="w-3.5 h-3.5 text-gray500" />
                                                                            ) : null}
                                                                            <ChevronRight className="w-3.5 h-3.5 text-textSecondary" />
                                                                        </div>
                                                                    )}
                                                                    {item.rightSideValue && item.rightSideValue}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default AiWorkflow;
